import { Component, OnInit, inject } from '@angular/core';
import { AppConfig } from "app/app.config";
import { LoaderService } from "app/services/loader.service";
import { APIService, pgfActivityType } from 'app/services/api.service';
import { AuthenticationService, reRgType } from 'app/services/auth.service';
import { styleType } from "app/components/Controls/NWMTable/NWMTable.component";
import { ExcelService } from "app/services/excel.service";
import { MenuComponent } from '../../../components/Controls/menu/menu.component';
import { FormsModule } from '@angular/forms';
import { NgClass, NgTemplateOutlet, CurrencyPipe } from '@angular/common';
import { NgbPagination, NgbPaginationFirst, NgbPaginationPrevious, NgbPaginationNext, NgbPaginationLast } from '@ng-bootstrap/ng-bootstrap';
import { RouterLink } from '@angular/router';
import { MinusSignToParens } from 'app/pipes/minus-sign-to-parens.pipe';

type sortFieldType = 'CharityName' | 'AmountCAD' | 'CharityType';

@Component({
    templateUrl: './grants.component.html',
    styleUrls: [`./grants.component.scss`],
    imports: [MenuComponent, FormsModule, NgClass, NgTemplateOutlet, NgbPagination, NgbPaginationFirst, NgbPaginationPrevious, NgbPaginationNext, NgbPaginationLast, RouterLink, MinusSignToParens, CurrencyPipe]
})

export class PgfGrantsComponent implements OnInit {
  private config = inject(AppConfig);
  private loaderService = inject(LoaderService);
  private excel = inject(ExcelService);
  private api = inject(APIService);
  auth = inject(AuthenticationService);

  public reRg?: reRgType;
  public page = 1;
  public pageSize: number = this.config.document.documentPageSize;
  public pageOptions: number[] = Array.from({length: 10}, (v, k) => (k + 1) * 10);
  public totalPages = 1;

  public filterText = '';
  public curentFilterText = '';
  public sortField: sortFieldType = 'AmountCAD';
  public sortDir = 1;

  public grantsList: pgfActivityType[] = [];
  public grantsLoaded = false;
  public filteredGrants: pgfActivityType[] = [];
  public pagedGrants: pgfActivityType[] = [];
  public total = 0;

  ngOnInit() {
    this.loaderService.display(true, 'grants');

    this.reRg = this.auth.getCurrentReRg()!;
    this.grantsLoaded = false;

    this.api.getPgfActivityByReRgId(this.reRg!.ReRgId).subscribe({
      next: res => {
        this.loaderService.display(false, 'grants');
        const grants: pgfActivityType[] = [];
        res.forEach(f=>{
          if (f.ActivityType == 'Grant') {
            const found = grants.find(g=>g.CharityName === f.CharityName);
            if (found) {
              found.AmountCAD+= f.AmountCAD;
            } else {
              grants.push({
                ...f
              });
            }
          }
        });
        this.grantsList = grants;
        this.filterGrants();
        this.grantsLoaded = true;
        },
        error: () => this.loaderService.display(false, 'grants')
    });

  }

  clearTextFilter() {
    this.curentFilterText = this.filterText = '';
    this.filterGrants();
  }

  filterGrants() {
    this.curentFilterText = this.filterText;
    const text = this.filterText.toLowerCase();
    this.total = 0;

    this.filteredGrants = this.grantsList.filter(f => {
      if (text=='' || f.CharityName.toLowerCase().includes(text)) {
        this.total+= f.AmountCAD;
        return true;
      }
      return false;
    })
    this.sortGrants();
    return true;
  }

  toggleSort(field: sortFieldType = 'AmountCAD') {
    if (this.sortField == field) {
      this.sortDir = 0-this.sortDir;
    } else {
      this.sortField = field;
      this.sortDir = 1;
    }
    this.sortGrants();
  }

  sortGrants() {
    if (this.sortField == 'CharityName') {
      this.filteredGrants.sort((a,b)=>
          a['CharityName'].localeCompare(b['CharityName'])*this.sortDir
      )
    } else {
      this.filteredGrants.sort((a,b)=>
          (a[this.sortField] || '') > (b[this.sortField] || '') ? 0-this.sortDir :
          (a[this.sortField] || '') < (b[this.sortField] || '') ? this.sortDir :
          a['CharityName'].localeCompare(b['CharityName'])
      )
    }
    this.setPage(1);
  }

  setPage(page: null | number = null) {
    if (page) this.page = page > 0 ? +page : 1;
    const l = this.filteredGrants.length;
    const p = this.pageSize>0 ? +this.pageSize : l;
    if (p<l) {
      this.totalPages = p ? Math.ceil(l / p) : 1;
      if (this.totalPages < this.page) this.page = this.totalPages;
      const s = (this.page-1) * p;
      const e = s+p > l ? l : s+p;
      this.pagedGrants = this.filteredGrants.slice(s, e);
    } else {
      this.totalPages = 1;
      this.pagedGrants = this.filteredGrants;
    }
  }

  export() {
    const headers = [ "Charity", "Impact Area","Amount"];
    const lastCol = headers.length-1;

    const rows = [
      [(this.reRg!.ClientName || '') + ' - Grants to all charities', '', this.excel.toExcelLocalTime(new Date())],
      [],
      headers,
    ];
    this.filteredGrants.forEach(t => {
      rows.push([t.CharityName, t.CharityType,t.AmountCAD]);
    });
    rows.push([],['Total Grants to Charities', '',this.total]);

    const styles: styleType[] = [
      // styles can be applied to row, col or cell, but if you do it more than once, only the latest will apply
      {col:0, font: "Arial 10 #000000", align: 'L C', width:80}, // make first column bold
      {row:0, col:1, font: "Arial 14 #000000 B"},
      {col:1, align: 'L C', width: 50 },
      {col:2, align: 'R C', width:20}, // right-center align numeric columns
      {row:2, height:30},
      {row:0, col:lastCol, format: "m-d-yyyy h:mm:ss AM/PM", font:'10'},
      {row:2, col:'0-'+lastCol, fill: "#183750", font: "Arial 10 #ffffff B", wrapText:1},
      {row:'3-'+rows.length,col:1, format: '$#,##0.00;(#,##0.00);&quot;-&quot;'},
      {row:rows.length-2, col:'0-'+lastCol, border: "none,none,thin #000000,none"},
      {row:rows.length-1, font: "Arial 10 #000000 B"}
    ];

    this.excel.generate('GrantsToCharities.xlsx', [{sheetTitle:'Grants to all charities', rows:rows, styles:styles}]);
  }
}
